import React, { useState } from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';
import { scroller } from 'react-scroll';
import PropTypes from 'prop-types';

import Container from 'brastrap/common/container/Container';
import Rating from 'brastrap/common/rating/Rating';
import Score from 'brastrap/promotional/score/Score';
import Meter from 'brastrap/promotional/meter/Meter';
import ResultsDisplay from 'brastrap/promotional/results-display/ResultsDisplay';
import Response from 'brastrap/common/response/Response';
import Pagination from 'brastrap/containers/pagination/PaginationContainer';
import ButtonLink from 'brastrap/common/button-link/ButtonLink';
import Message from 'brastrap/common/message/Message';
import Activity from 'brastrap/common/activity/Activity';
import ShowMoreButton from 'brastrap/common/show-more-button/ShowMoreButton';
import Link from 'brastrap/containers/link/LinkContainer';

const messages = defineMessages({
  title: {
    id: 'reviews.page-title',
    defaultMessage: 'Latest reviews for the {name}',
  },
  allColours: { id: 'reviews.all-colours', defaultMessage: 'All colours' },
  showReviewsFor: {
    id: 'reviews.showReviewsBy',
    defaultMessage: 'Show reviews by:',
  },
  callToAction: { id: 'reviews.write-cta', defaultMessage: 'write a review' },
  noReviews: {
    id: 'reviews.empty',
    defaultMessage: 'No reviews found. Be the first to {link}.',
  },
  meterTitle: { id: 'rating.meter.title', defaultMessage: 'True to size?' },
});

const scoreLabels = {
  1: '1 star',
  2: '2 star',
  3: '3 star',
  4: '4 star',
  5: '5 star',
};

const meterLabels = defineMessages({
  start: { id: 'rating.meter.start', defaultMessage: 'Small' },
  middle: { id: 'rating.meter.middle', defaultMessage: 'True' },
  end: { id: 'rating.meter.end', defaultMessage: 'Big' },
});

/**
 * @param {Function} formatMessage
 * @returns {Array}
 */
const formatMeterExtremes = formatMessage =>
  Object.keys(meterLabels).map(value => ({
    position: value,
    text: formatMessage(meterLabels[value]),
  }));

const showOptions = [4, 8, 16];

const CONTAINER_ID = 'reviews';
const WRITE_REVIEW_PATH = 'write-review/';

/**
 * Message displayed when we have no reviews.
 *
 * @constructor
 */
const NoReviews = () => (
  <div className="c-reviews__no-reviews">
    <Message modifiers={['inform']} dismissable={false}>
      <FormattedMessage
        {...messages.noReviews}
        values={{
          link: (
            <Link to={WRITE_REVIEW_PATH} rel="nofollow">
              <FormattedMessage {...messages.callToAction} />
            </Link>
          ),
        }}
      />
    </Message>
  </div>
);

/**
 * The component to be displayed while the reviews are loading.
 *
 * @constructor
 */
const Loading = () => (
  <div className="c-reviews__loading">
    <Activity />
  </div>
);

/**
 * @param {Object} props
 * @returns {XML}
 * @constructor
 */
const ReviewsContainer = props => {
  const {
    intl: { formatMessage },
    filterOptions,
    filter,
    limit,
    loading,
    page,
    linkType,
    rating,
    reviews,
    sortBy,
    starRating,
    styleName,
    isAllReviews,
    isMobile,
  } = props;

  const displayReviews = !loading && !!reviews.length;
  const displayNoReviews = !loading && !reviews.length;
  const displayRatings = !!rating.total;

  const ratingLabel = rating.value ? `${rating.value.toFixed(1)} / 5` : null;

  const [styleColourCodeFilter, setStyleColourCodeFilter] = useState('all');
  const [starRatingFilter, setStarRatingFilter] = useState('all');
  const [showMoreReviews, setShowMoreReviews] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const scrollToReviews = () => {
    scroller.scrollTo(CONTAINER_ID, {
      duration: 100,
      smooth: true,
    });
  };

  const reverseStarRatingOrder = obj => {
    const starRatings = [];

    Object.keys(obj)
      .sort()
      .reverse()
      .forEach(key => {
        starRatings.push({
          key: key,
          value: obj[key],
        });
      });

    return starRatings;
  };

  let starRatings;
  if (rating.starDistribution) {
    starRatings = reverseStarRatingOrder(rating.starDistribution);
  }
  const ratingFilter = [
    { label: 'All Ratings', value: 'all' },
    { label: '5 star', value: 5 },
    { label: '4 star', value: 4 },
    { label: '3 star', value: 3 },
    { label: '2 star', value: 2 },
    { label: '1 star', value: 1 },
  ];

  const ratingOptions = [...ratingFilter];
  const ratingSort = starRating || ratingOptions[0].value;
  const formatValue = value => {
    return ((value / rating.total) * 100).toFixed(2);
  };

  const containerTitle = isAllReviews
    ? ''
    : formatMessage(messages.title, { name: styleName });

  const options = [...filterOptions];
  options.push({ label: formatMessage(messages.allColours), value: 'all' });
  const currentSort = sortBy || options[0].value;
  return (
    <Container
      id={CONTAINER_ID}
      modifiers={['ornamented']}
      title={containerTitle}
    >
      {isAllReviews && (
        <ResultsDisplay
          limit={limit}
          onChange={filter}
          sortBy={sortBy}
          showOptions={showOptions}
          sortOptions={options}
          sortMessage={formatMessage(messages.showReviewsFor)}
          isAllReviews={isAllReviews}
        />
      )}
      {displayRatings && !isAllReviews && !isMobile && (
        <div className="c-results-container">
          <div className="c-rating__container">
            <div className="l-grid">
              <div className="l-grid__unit l-grid__unit--padding-bottom">
                <span className="c-rating__label">{ratingLabel}</span>
                <Rating {...rating} size="large" />
              </div>
            </div>
            {starRatings &&
              starRatings.map(({ key, value }) => (
                <div className="l-grid" style={{ marginTop: 0 }}>
                  <div key={key} className="l-grid__unit">
                    <Score
                      title={scoreLabels[key]}
                      value={formatValue(value)}
                    />
                  </div>
                </div>
              ))}
            {rating.fit >= 0 && (
              <div className="l-grid" style={{ marginTop: 0 }}>
                <div className="l-grid__unit">
                  <Meter
                    extremes={formatMeterExtremes(formatMessage)}
                    title={formatMessage(messages.meterTitle)}
                    value={rating.fit}
                  />
                </div>
              </div>
            )}
          </div>
          {!isAllReviews && (
            <div className="c-filters-container">
              <div className="c-results-display__item">
                <label
                  className="c-results-display__label--bold"
                  htmlFor={'sort-results'}
                >
                  {formatMessage(messages.showReviewsFor)}
                </label>
                {/* This is a new class for reviews on the PDP only */}
                <div className="c-results-display__filter-options">
                  <select
                    onChange={e => {
                      e.preventDefault();
                      setStarRatingFilter(e.currentTarget.value);
                      props.filter({
                        starRating: e.currentTarget.value,
                        sortBy: styleColourCodeFilter,
                        limit,
                      });
                      scrollToReviews();
                    }}
                    value={ratingSort}
                    className="c-field-select"
                    id={'rating-filter'}
                  >
                    {ratingOptions.map((option, key) => {
                      const optionProps = {
                        key,
                        value: option.value,
                      };
                      return <option {...optionProps}>{option.label}</option>;
                    })}
                  </select>
                  <select
                    onChange={e => {
                      setStyleColourCodeFilter(e.currentTarget.value);
                      e.preventDefault();
                      props.filter({
                        sortBy: e.currentTarget.value,
                        starRating: starRatingFilter,
                        limit,
                      });
                      scrollToReviews();
                    }}
                    value={currentSort}
                    className="c-field-select"
                    id={'colour-filter'}
                  >
                    {options.map((option, key) => {
                      const optionProps = {
                        key,
                        value: option.value,
                      };
                      return <option {...optionProps}>{option.label}</option>;
                    })}
                  </select>
                </div>
              </div>
              <div className="c-writeReview">
                <ButtonLink
                  modifiers={['write-review']}
                  url={WRITE_REVIEW_PATH}
                  rel="nofollow"
                  label={formatMessage(messages.callToAction)}
                  linkType={linkType}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {displayRatings && !isAllReviews && isMobile && (
        <div className="c-results-container--mobile">
          <div className="c-rating__container--mobile">
            <div className="l-grid">
              <div className="l-grid__unit l-grid__unit--padding-bottom">
                <span className="c-rating__label">{ratingLabel}</span>
                <Rating {...rating} size="mobile" />
              </div>
            </div>
            <div className="c-starRating__container">
              {starRatings &&
                starRatings.map(({ key, value }) => (
                  <div className="l-grid" style={{ marginTop: 0 }}>
                    <div key={key} className="l-grid__unit">
                      <Score
                        title={scoreLabels[key]}
                        value={formatValue(value)}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
          {rating.fit >= 0 && (
            <div className="l-grid" style={{ marginTop: 0 }}>
              <div className="l-grid__unit">
                <Meter
                  extremes={formatMeterExtremes(formatMessage)}
                  title={formatMessage(messages.meterTitle)}
                  value={rating.fit}
                />
              </div>
            </div>
          )}
          <ShowMoreButton
            onToggleButton={() => setShowMoreFilters(x => !x)}
            showMoreLabel={showMoreFilters ? 'HIDE FILTERS' : 'SHOW FILTERS'}
            isFilterButton
          />
          {showMoreFilters && !isAllReviews && (
            <div className="c-filters-container">
              <div className="c-results-display__item">
                <label
                  className="c-results-display__label--bold"
                  htmlFor={'sort-results'}
                >
                  {formatMessage(messages.showReviewsFor)}
                </label>
                {/* This is a new class for reviews on the PDP only */}
                <div className="c-results-display__filter-options">
                  <select
                    onChange={e => {
                      e.preventDefault();
                      setStarRatingFilter(e.currentTarget.value);
                      props.filter({
                        starRating: e.currentTarget.value,
                        sortBy: styleColourCodeFilter,
                        limit,
                      });
                      scrollToReviews();
                    }}
                    value={ratingSort}
                    className="c-field-select"
                    id={'rating-filter'}
                  >
                    {ratingOptions.map((option, key) => {
                      const optionProps = {
                        key,
                        value: option.value,
                      };
                      return <option {...optionProps}>{option.label}</option>;
                    })}
                  </select>
                  <select
                    onChange={e => {
                      setStyleColourCodeFilter(e.currentTarget.value);
                      e.preventDefault();
                      props.filter({
                        sortBy: e.currentTarget.value,
                        starRating: starRatingFilter,
                        limit,
                      });
                      scrollToReviews();
                    }}
                    value={currentSort}
                    className="c-field-select"
                    id={'colour-filter'}
                  >
                    {options.map((option, key) => {
                      const optionProps = {
                        key,
                        value: option.value,
                      };
                      return <option {...optionProps}>{option.label}</option>;
                    })}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {loading && <Loading />}
      {displayNoReviews && <NoReviews />}
      {displayReviews && !isAllReviews && isMobile && (
        <div>
          {!showMoreReviews && (
            <Response key={reviews[0].review.date} {...reviews[0]} preview />
          )}
          {showMoreReviews && displayReviews && (
            <div>
              {reviews.map((item, index) => (
                <Response key={index + item.review.date} {...item} />
              ))}
              <Pagination
                maxItems={8}
                page={page}
                total={Math.ceil(rating.total / limit)}
                onClick={pageNo => {
                  filter({ page: pageNo, limit });
                  scroller.scrollTo(CONTAINER_ID, {
                    duration: 400,
                    smooth: true,
                  });
                }}
                scroll={{ target: CONTAINER_ID }}
              />
            </div>
          )}
          <ShowMoreButton
            onToggleButton={() => setShowMoreReviews(x => !x)}
            showMoreLabel={
              showMoreReviews ? 'HIDE REVIEWS' : 'SHOW MORE REVIEWS'
            }
          />
          <div className="c-writeReview">
            <ButtonLink
              modifiers={['write-review']}
              url={WRITE_REVIEW_PATH}
              rel="nofollow"
              label={formatMessage(messages.callToAction)}
              linkType={linkType}
            />
          </div>
        </div>
      )}
      {displayReviews && !isAllReviews && !isMobile && (
        <div>
          {reviews.map((item, index) => (
            <Response key={index + item.review.date} {...item} />
          ))}
          <Pagination
            maxItems={8}
            page={page}
            total={Math.ceil(rating.total / limit)}
            onClick={pageNo => {
              filter({ page: pageNo, limit });
              scroller.scrollTo(CONTAINER_ID, {
                duration: 400,
                smooth: true,
              });
            }}
            scroll={{ target: CONTAINER_ID }}
          />
        </div>
      )}
      {displayReviews && isAllReviews && (
        <div>
          {reviews.map((item, index) => (
            <Response key={index + item.review.date} {...item} />
          ))}
          <Pagination
            isAllReviews
            maxItems={8}
            page={page}
            total={Math.ceil(rating.total / limit)}
            onClick={pageNo => {
              filter({ page: pageNo, limit });
              scroller.scrollTo(CONTAINER_ID, { duration: 400, smooth: true });
            }}
            scroll={{ target: CONTAINER_ID }}
          />
        </div>
      )}
    </Container>
  );
};

ReviewsContainer.propTypes = {
  intl: intlShape,
  filterOptions: PropTypes.array,
  filter: PropTypes.func,
  limit: PropTypes.number,
  loading: PropTypes.bool,
  page: PropTypes.number,
  linkType: PropTypes.string,
  rating: {
    fit: PropTypes.number,
    style: PropTypes.number,
    trueToSize: PropTypes.number,
    total: PropTypes.number,
    value: PropTypes.number,
    comfort: PropTypes.number,
    starDistribution: PropTypes.object,
  },
  reviews: PropTypes.array,
  sortBy: PropTypes.string,
  starRating: PropTypes.string,
  styleName: PropTypes.string,
  isYotpoEnabled: PropTypes.bool,
  isAllReviews: PropTypes.bool,
  isMobile: PropTypes.bool,
};

ReviewsContainer.defaultProps = {
  filterOptions: [],
  rating: {},
  reviews: [],
  isYotpoEnabled: false,
  isAllReviews: false,
};

export default injectIntl(ReviewsContainer);
